import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { AnonymousQuestion } from "../../../types/dashboard";
import { useAppDispatch } from "../../../hooks/redux";
import { deleteAnonymousQuestionAction } from "../../../store/dashboardSlice";
import {
  StyledAnonymousQuestionItem,
  TrashButton,
  ArrowIcon,
  TotalQuestionsBadge,
  CreatedDate,
} from "./index.styled";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import trashIcon from "../../../assets/icons/trash.png";
import arrowIcon from "../../../assets/icons/arrow-down.png";

interface AnonymousQuestionItemProps {
  question: AnonymousQuestion;
}

const AnonymousQuestionItem = ({ question }: AnonymousQuestionItemProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "--/--/----";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return "--/--/----";
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!question.id) return;
    try {
      await dispatch(deleteAnonymousQuestionAction(question.id)).unwrap();
      setIsDeleteModalOpen(false);
    } catch (error: unknown) {
      console.error("Failed to delete anonymous question", error);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleQuestionClick = () => {
    if (question.short_code) {
      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get("token");
      const path = token
        ? `/anonymous/${question.short_code}?token=${token}`
        : `/anonymous/${question.short_code}`;
      navigate(path);
    }
  };

  return (
    <>
      <StyledAnonymousQuestionItem onClick={handleQuestionClick}>
        <TrashButton
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick();
          }}
          title="מחיקה"
        >
          <img src={trashIcon} alt="delete" />
        </TrashButton>
        <div className="question-content">
          {question.content?.replace(/<br\s*\/?>/gi, "\n") ?? ""}
        </div>
        {!!question.total_questions && (
          <TotalQuestionsBadge>{question.total_questions}</TotalQuestionsBadge>
        )}
        <CreatedDate>{formatDate(question.created_at)}</CreatedDate>
        <ArrowIcon src={arrowIcon} alt="" />
      </StyledAnonymousQuestionItem>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="מחיקת שאלה"
        description="האם אתה בטוח שברצונך למחוק את השאלה הזו?"
      />
    </>
  );
};

export default AnonymousQuestionItem;
